import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, CalendarClock, History, Bell, AppWindow, Loader2, Columns3 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { cleanSenderLabel } from '@/lib/utils/format';
import { useConversations } from '@/hooks/use-conversations';
import { useSenders } from '@/hooks/use-senders';
import { usePermissions } from '@/hooks/use-permissions';
import { useUIStore } from '@/stores/ui-store';
import { useModuleStore } from '@/stores/module-store';
import { useConversationStore } from '@/stores/conversation-store';
import { useMessageStore } from '@/stores/message-store';
import { useSettingsStore } from '@/stores/settings-store';
import { useAppStore } from '@/stores/app-store';
import { useLookupStore } from '@/stores/lookup-store';
import { applyCountryCode, isFullInternationalNumber } from '@/lib/utils/phone';

import { EmptyState } from '@/components/common/empty-state';
import { ConversationList } from './conversation-list';
import { ChatThread } from './chat-thread';
import { InboxPanel } from './inbox-panel';
import { LookupPanel } from './lookup-panel';
import { LookupPanelIframe } from './lookup-panel-iframe';
import { EnrichmentModulesPicker } from './enrichment-modules-picker';
import { NewChatDialog } from './new-chat-dialog';
import { RemindersDialog } from './reminders-dialog';
import { LOOKUP_SUPPORTED_SAAS, LOOKUP_IFRAME_SAAS } from '@/modules/tpa/lookup/lookup-types';
import type { Channel, Conversation } from '@/types/messaging';

const ALL_SENDERS_VALUE = '__all__';

export function ConversationsView() {
  const {
    conversations,
    activeConversation,
    activeSender,
    messages,
    loading,
    loadingMore,
    messagesLoading,
    hasMore,
    searchQuery,
    filterLabel,
    filterAgent,
    jumpBefore,
    chatLabels,
    error,
    phoneSearchActive,
    setActiveConversation,
    setActiveSender,
    setSearchQuery,
    setFilterChannel,
    setFilterLabel,
    setFilterAgent,
    jumpToConversationsAtDate,
    searchByPhone,
    refresh,
    loadMore,
    markAsRead,
  } = useConversations();

  const { senders, allSenders, loading: sendersLoading } = useSenders('inbox');
  const { isAdmin } = usePermissions();
  const tpaModule = useModuleStore((s) => s.tpaModule);
  const tpaAuthenticated = useModuleStore((s) => s.tpaAuthenticated);

  // All senders are restricted — don't load or show conversations
  const allSendersHidden = !isAdmin && !sendersLoading && allSenders.length > 0 && senders.length === 0;

  // Prefetch templates into the module's in-memory cache at app load
  // (same timing as senders). This populates templatesCacheResult so HSM
  // messages in conversation history show resolved template body text.
  const templatesPrefetched = useRef(false);
  useEffect(() => {
    if (templatesPrefetched.current || !tpaModule || !tpaAuthenticated) return;
    templatesPrefetched.current = true;
    tpaModule.getTemplates().catch(() => {});
  }, [tpaModule, tpaAuthenticated]);
  const tpaLoading = useModuleStore((s) => s.tpaLoading);
  const saasModule = useModuleStore((s) => s.saasModule);
  const appCodeSAAS = useAppStore((s) => s.appCodeSAAS);
  const setView = useUIStore((s) => s.setView);
  const lookupPanelOpen = useUIStore((s) => s.lookupPanelOpen);
  const setLookupPanelOpen = useUIStore((s) => s.setLookupPanelOpen);
  const lookupSupported = !!(appCodeSAAS && LOOKUP_SUPPORTED_SAAS.has(appCodeSAAS));
  // These CRMs run LookUp in its own iframe so it can hold its own OAuth
  // token, separate from this app's — see `LOOKUP_IFRAME_SAAS`.
  const lookupUsesIframe = !!(appCodeSAAS && LOOKUP_IFRAME_SAAS.has(appCodeSAAS));
  const refreshMessages = useConversationStore((s) => s.refreshMessages);

  const preferences = useSettingsStore((s) => s.preferences);
  const settingsLoaded = useSettingsStore((s) => s.settingsLoaded);
  const didInitialFetch = useRef(false);

  // Track whether the first conversation API response has been received.
  // Skeleton stays visible from page load until this becomes true.
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Track if we need a conversations refresh after senders finish loading.
  // This handles the case where the initial fetch returned empty because
  // channels weren't ready yet (matching original app's TPA_SENDERS_FETCH_COMPLETED pattern).
  const needsSenderRefresh = useRef(false);

  // ── Apply inbox default sender and fetch conversations ───────────────
  // Uses cached sender details from preferences (inboxDefaultSender) so we
  // don't need to wait for the async getSenders() call to complete.
  // Only requires TPA + settings to be ready.
  //
  // When widget context is active (CRM record open), only load conversations
  // for the specific contact's phone number — not all conversations.
  // Old code: fetchConversationsAndAddToChatBox filters by targetNumber.
  const crmRecordContext = useUIStore((s) => s.crmRecordContext);
  const crmPhoneFields = useUIStore((s) => s.crmPhoneFields);
  const saasAuthenticated = useModuleStore((s) => s.saasAuthenticated);
  const saasAuthChecked = useModuleStore((s) => s.saasAuthChecked);
  const crmRecordsLoading = useModuleStore((s) => s.crmRecordsLoading);

  useEffect(() => {
    if (didInitialFetch.current) return;
    if (!tpaModule || !tpaAuthenticated) return;
    if (!settingsLoaded) return; // Wait for Firestore settings to load
    if (allSendersHidden) return; // All senders restricted — don't fetch

    // If CRM records are still loading (SDK widget context being resolved),
    // wait before deciding whether to load all or filtered conversations.
    if (!crmRecordContext && crmRecordsLoading) {
      return;
    }

    // When CRM record context is active, wait for phone fields then search by phone.
    // Multiple entity IDs → multiple phone fields — search all of them.
    if (crmRecordContext) {
      // If SaaS auth has been checked and failed, or CRM records finished loading
      // with no phone fields — don't block forever, fall through to normal flow.
      const saasResolved = saasAuthChecked && !saasAuthenticated;
      const crmDoneNoPhones = saasAuthChecked && !crmRecordsLoading && crmPhoneFields.length === 0;

      if (crmPhoneFields.length > 0) {
        // Phone fields available — search by phone
        didInitialFetch.current = true;
        // Apply default country code first so a CRM-stored local number
        // (e.g. "5551234567") becomes the full E.164 ("+15551234567")
        // before we strip to digits — otherwise Twilio lookup misses messages.
        const defaultCC = preferences.defaultCountryCode;
        const uniquePhones = new Set<string>();
        for (const field of crmPhoneFields) {
          if (!field.value) continue;
          const normalized = applyCountryCode(field.value, defaultCC);
          const digits = normalized.replace(/\D/g, '');
          // Keep the '+' so searchByPhone treats the number as final instead
          // of re-applying the default CC to an already-normalized value.
          if (digits) uniquePhones.add(`+${digits}`);
        }
        const allPhones = Array.from(uniquePhones).join(',');
        if (allPhones) {
          searchByPhone(allPhones);
        }
        return;
      }

      if (!saasResolved && !crmDoneNoPhones) {
        // Still waiting for SaaS auth / CRM record loading — keep showing skeleton
        return;
      }

      // SaaS auth failed or CRM records loaded with no phone numbers —
      // fall through to normal conversation loading below
      console.log('[ConversationsView] CRM context active but no phone fields resolved, falling back to normal flow',
        { saasAuthChecked, saasAuthenticated, crmRecordsLoading, crmPhoneFieldsCount: crmPhoneFields.length });
    }

    didInitialFetch.current = true;

    const cachedSender = preferences.inboxDefaultSender;
    if (cachedSender) {
      setActiveSender({
        id: cachedSender.id,
        label: cachedSender.label,
        channel: cachedSender.channel as Channel,
        phoneNumber: cachedSender.phoneNumber,
        data: {},
      });
      return;
    }

    // No default set — fetch all conversations (no sender filter)
    // Mark that we may need to re-fetch once senders are loaded
    needsSenderRefresh.current = true;
    refresh();
  }, [settingsLoaded, preferences.inboxDefaultSender, setActiveSender, tpaModule, tpaAuthenticated, refresh, crmRecordContext, crmPhoneFields, searchByPhone, saasAuthenticated, saasAuthChecked, crmRecordsLoading, allSendersHidden]);

  // ── Re-fetch conversations once senders finish loading ───────────────
  // Matches the original app's TPA_SENDERS_FETCH_COMPLETED → getConversationsList() pattern.
  // If the initial fetch returned 0 conversations AND senders are now available,
  // re-trigger the fetch since channels are now populated in the TPA module.
  useEffect(() => {
    if (!needsSenderRefresh.current) return;
    if (sendersLoading) return; // Still loading senders
    if (senders.length === 0) return; // Senders loaded but empty — nothing to retry with
    if (loading) return; // Conversations still loading

    // Senders are ready and conversations may have been empty — re-fetch
    if (conversations.length === 0) {
      console.log('[ConversationsView] Senders loaded, re-fetching conversations...');
      needsSenderRefresh.current = false;
      refresh();
    } else {
      needsSenderRefresh.current = false;
    }
  }, [senders, sendersLoading, loading, conversations.length, refresh]);

  // Mark initial load complete once the first API response arrives.
  // This covers both "conversations loaded" and "API returned empty".
  useEffect(() => {
    if (initialLoadComplete) return;
    // Once the initial fetch has been triggered AND loading finishes → API responded
    if (didInitialFetch.current && !loading) {
      setInitialLoadComplete(true);
    }
  }, [loading, initialLoadComplete]);

  // Auto-select the single sender so conversations are filtered to it
  // (when there's only one sender, the dropdown is hidden — see ConversationList)
  useEffect(() => {
    if (senders.length === 1 && !activeSender) {
      setActiveSender(senders[0]);
    }
  }, [senders, activeSender, setActiveSender]);

  const isWhatsApp = senders[0]?.channel === 'WHATSAPP';
  const senderOptions = [
    { value: ALL_SENDERS_VALUE, label: isWhatsApp ? 'All WABA numbers' : 'All senders' },
    ...senders.map((s) => ({
      value: s.id,
      label: cleanSenderLabel(s.label),
      description: s.phoneNumber || s.channel,
    })),
  ];

  const setSender = useMessageStore((s) => s.setSender);
  const mentions = useConversationStore((s) => s.mentions);
  const [showNewChat, setShowNewChat] = useState(false);
  const [showReminders, setShowReminders] = useState(false);
  const [emptyStateView, setEmptyStateView] = useState<'none' | 'scheduled' | 'history'>('none');

  const handleSelectConversation = useCallback(
    (conv: typeof conversations[0]) => {
      // Placeholder conversations (e.g. "Start WhatsApp conversation") have no messages
      // on the server yet — skip fetchMessages to avoid a wasted API call.
      const isPlaceholder = conv.contactName.startsWith('Start ') && conv.contactName.endsWith(' conversation');
      if (isPlaceholder) {
        useConversationStore.setState({
          activeConversation: conv,
          messages: [],
          messagesHasMore: false,
          loadingOlderMessages: false,
          realtimeMessageIds: new Set<string>(),
        });
      } else {
        setActiveConversation(conv);
      }
      // Inbox-side rule: only fire mark-as-read when there's an unread
      // badge on this conv. `markAsRead` clears the badge locally,
      // broadcasts to other tabs, and fires the server CF in one shot.
      if (conv.unreadCount > 0) {
        markAsRead(conv.id);
      }
      // Stay in CONVERSATIONS view — the right panel shows the chat thread.
      // Only switch to CHAT if not already in conversations view (e.g. called from mentions).
      if (useUIStore.getState().activeView !== 'CONVERSATIONS') {
        setView('CHAT');
      }
      setEmptyStateView('none');

      // Auto-set the sender from the conversation.
      // Method 1: Parse senderNumber::contactNumber format (Twilio, WhatsApp App)
      // Method 2: Use lastUsedChannelId from metadata (MessageBird REST — matches original
      //   messagebird-rest-things.js line 1892: chooseMessageSender(lastUsedChannelId))
      let matchedSender: typeof senders[0] | undefined;

      // Auto-set the sender from the conversation ID (format: senderNumber::contactNumber)
      // This removes the need to manually choose a sender in Compose for replies.
      // When SMS and WhatsApp senders share the same phone number, use the
      // conversation's channel to pick the correct one.
      const parts = (conv.id || '').split('::');
      if (parts.length >= 2) {
        const senderNumber = parts[0]; // e.g. "919876543210" or "whatsapp:+1234567890"
        const senderDigits = senderNumber.replace(/\D/g, '');
        const isWhatsAppConv = conv.channel === 'WHATSAPP' || senderNumber.startsWith('whatsapp:');
        matchedSender = senders.find((s) => {
          const sPhone = s.phoneNumber || '';
          const sDigits = sPhone.replace(/\D/g, '');
          const sMsisdn = (s.data?.msisdn as string) || '';
          const phoneMatch = sDigits === senderDigits
            || sMsisdn === senderDigits
            || sPhone === senderNumber
            || senderNumber === `whatsapp:${sPhone}`;
          if (!phoneMatch) return false;
          // When channel info is available, prefer the sender whose channel matches
          // the conversation channel (avoids selecting SMS sender for WhatsApp conv)
          if (isWhatsAppConv) return s.channel === 'WHATSAPP';
          if (conv.channel && s.channel) return s.channel === conv.channel;
          return true;
        })
        // Fallback: if channel-strict match found nothing, match by phone only
        || senders.find((s) => {
          const sPhone = s.phoneNumber || '';
          const sDigits = sPhone.replace(/\D/g, '');
          const sMsisdn = (s.data?.msisdn as string) || '';
          return sDigits === senderDigits
            || sMsisdn === senderDigits
            || sPhone === senderNumber
            || senderNumber === `whatsapp:${sPhone}`;
        });
      }

      // Fallback: match by lastUsedChannelId (MessageBird REST conversations use UUIDs as IDs,
      // not senderNumber::contactNumber format, so Method 1 won't work for them)
      if (!matchedSender && conv.metadata?.lastUsedChannelId) {
        const channelId = conv.metadata.lastUsedChannelId as string;
        matchedSender = senders.find((s) => s.id === channelId);
      }

      if (matchedSender) {
        setSender(matchedSender);
      }
    },
    [setActiveConversation, markAsRead, setView, senders, setSender]
  );

  const handleNewChat = useCallback((phone: string, channel: Channel, senderId: string) => {
    const matchedSender = senders.find((s) => s.id === senderId);
    if (!matchedSender) return;
    const cleanPhone = phone.replace(/\D/g, '');
    const cleanSenderPhone = (matchedSender.phoneNumber || '').replace(/\D/g, '');
    const currentTpaId = tpaModule?.id;

    // Build conversation ID based on TPA format:
    // WhatsApp app: {digits}::{digits}
    // Twilio SMS:   +{digits}::+{digits}
    // Twilio WhatsApp channel: whatsapp:+{digits}::whatsapp:+{digits}
    let senderNum: string;
    let recipientNum: string;
    if (currentTpaId === 'whatsapp') {
      senderNum = cleanSenderPhone;
      recipientNum = cleanPhone;
    } else if (channel === 'WHATSAPP') {
      senderNum = `whatsapp:+${cleanSenderPhone}`;
      recipientNum = `whatsapp:+${cleanPhone}`;
    } else {
      senderNum = `+${cleanSenderPhone}`;
      recipientNum = `+${cleanPhone}`;
    }
    const convId = `${senderNum}::${recipientNum}`;

    // Check if a conversation with this ID (or its reverse) already exists
    const normalizeId = (id: string) => id.split('::').sort().join('::');
    const normTarget = normalizeId(convId);
    const existingConv = conversations.find(
      (c) => c.id === convId || normalizeId(c.id) === normTarget
    );

    if (existingConv) {
      // Conversation exists in list — select it normally (loads messages with standard flow)
      handleSelectConversation(existingConv);
      setSender(matchedSender);
      setShowNewChat(false);
      return;
    }

    // Brand new conversation — set active without loading spinner
    const newConv: Conversation = {
      id: convId,
      contactName: `+${cleanPhone}`,
      contactPhone: cleanPhone,
      lastMessage: '',
      lastMessageTime: new Date(),
      unreadCount: 0,
      channel,
    };
    // Set active conversation directly (no messagesLoading indicator)
    useConversationStore.setState({
      activeConversation: newConv,
      messages: [],
      messagesHasMore: false,
      loadingOlderMessages: false,
    });
    // Try loading existing messages silently in the background
    const currentTpaModule = useModuleStore.getState().tpaModule;
    if (currentTpaModule) {
      currentTpaModule.getMessages(convId, { pageSize: 50 })
        .then((msgs) => {
          if (!msgs || msgs.length === 0) return;
          // Only update if this conversation is still active
          if (useConversationStore.getState().activeConversation?.id === convId) {
            const sorted = [...msgs].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
            useConversationStore.setState({
              messages: sorted,
              messagesHasMore: msgs.length >= 20,
            });
          }
        })
        .catch(() => { /* fail silently */ });
    }
    setSender(matchedSender);
    setView('CHAT');
    setShowNewChat(false);
  }, [senders, conversations, handleSelectConversation, setSender, setView, tpaModule]);

  // ── Auto-open the CRM lookup panel for standalone Inbox conversations ──
  // When a conversation is selected outside a CRM widget context (i.e. plain
  // Inbox view), there's no record info loaded yet — auto-opening the lookup
  // panel surfaces the matched CRM contact immediately so the agent can act
  // on it (and so the sent-message note has a record to attach to).
  //
  // Gated on the user-configurable `showByDefault` preference (Settings →
  // Inbox View → "Show Lookup … panel by default"). Uses a ref so we only
  // auto-open ONCE per conversation selection — if the user hits the
  // hide-panel button we respect that and don't re-open on the next render
  // (`lookupPanelOpen` intentionally NOT in deps).
  const lookupShowByDefault = useLookupStore((s) => s.showByDefault);
  const lookupSettingsLoaded = useLookupStore((s) => s.lookupSettingsLoaded);
  const loadLookupSettings = useLookupStore((s) => s.loadLookupSettings);

  // Eagerly hydrate lookup settings from Firestore when the Inbox view mounts
  // and the SaaS is ready. Without this, `lookupSettingsLoaded` stays false
  // until the lookup panel is first opened — which blocks the auto-open
  // effect below (catch-22). Cheap: one Firestore read, cached via SWR.
  useEffect(() => {
    if (!lookupSupported) return;
    if (!saasAuthenticated) return;
    if (lookupSettingsLoaded) return;
    loadLookupSettings();
  }, [lookupSupported, saasAuthenticated, lookupSettingsLoaded, loadLookupSettings, activeConversation?.id]);

  const autoOpenedForConvIdRef = useRef<string | null>(null);
  useEffect(() => {
    const convId = activeConversation?.id ?? null;
    if (!convId) return;
    if (crmRecordContext) return;        // already in a CRM record context
    if (!lookupSupported) return;        // SaaS doesn't support lookup
    if (!saasAuthenticated) return;      // can't query without auth
    if (!lookupSettingsLoaded) return;   // wait for prefs to hydrate
    if (!lookupShowByDefault) return;    // user turned it off
    if (autoOpenedForConvIdRef.current === convId) return;
    autoOpenedForConvIdRef.current = convId;
    setLookupPanelOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversation?.id, crmRecordContext, lookupSupported, saasAuthenticated, lookupSettingsLoaded, lookupShowByDefault, setLookupPanelOpen]);

  // ── Re-match sender when senders load after conversation was already selected ──
  // Solves timing: user clicks a conversation before async getSenders() completes.
  // Also re-matches when the current sender isn't the conversation's own number
  // (e.g. the default/test sender auto-selected while senders were still loading,
  // or an SMS sender selected for a WhatsApp conversation sharing the same number).
  const currentSender = useMessageStore((s) => s.sender);
  useEffect(() => {
    if (!activeConversation || senders.length === 0) return;

    const parts = activeConversation.id.split('::');
    if (parts.length < 2) return;

    const senderNumber = parts[0];
    const senderDigits = senderNumber.replace(/\D/g, '');

    const matchesConvNumber = (s: typeof senders[0]): boolean => {
      const sPhone = s.phoneNumber || '';
      const sDigits = sPhone.replace(/\D/g, '');
      const sMsisdn = (s.data?.msisdn as string) || '';
      return (
        sDigits === senderDigits ||
        sMsisdn === senderDigits ||
        sPhone === senderNumber ||
        senderNumber === `whatsapp:${sPhone}`
      );
    };

    // Skip only if already correct — "correct" means the selected sender IS the
    // conversation's own number, not merely a sender on the same channel. In a
    // WABA app every sender shares channel WHATSAPP (including Meta's test
    // number auto-selected as default while getSenders() was in flight), so a
    // channel-only check kept the wrong sender selected and replies failed with
    // "(#131030) Recipient phone number not in allowed list".
    if (currentSender) {
      const channelOk =
        !activeConversation.channel ||
        currentSender.channel === activeConversation.channel;
      if (senderDigits ? matchesConvNumber(currentSender) && channelOk : channelOk) {
        return;
      }
    }

    const isWhatsAppConv =
      activeConversation.channel === 'WHATSAPP' ||
      senderNumber.startsWith('whatsapp:');

    let matched: typeof senders[0] | undefined;

    // 1. Channel-aware match (BEST MATCH)
    matched = senders.find((s) => {
      if (!matchesConvNumber(s)) return false;
      if (isWhatsAppConv) return s.channel === 'WHATSAPP';
      if (activeConversation.channel && s.channel) {
        return s.channel === activeConversation.channel;
      }
      return true;
    });

    // 2. Fallback: phone-only match
    if (!matched) {
      matched = senders.find(matchesConvNumber);
    }

    // 3. Fallback: lastUsedChannelId
    if (!matched && activeConversation.metadata?.lastUsedChannelId) {
      matched = senders.find(
        (s) => s.id === activeConversation.metadata?.lastUsedChannelId
      );
    }

    if (matched) {
      setSender(matched);
    }
  }, [activeConversation, currentSender, senders, setSender]);

  const handleBack = useCallback(() => {
    setActiveConversation(null);
    setView('CONVERSATIONS');
  }, [setActiveConversation, setView]);

  const handleMessageSent = useCallback(() => {
    // No-op: the chat-input now fetches the sent message by SID
    // and updates the conversation subject directly.
    // A full conversation list refresh is no longer needed.
  }, []);

  // Conversation list shows skeleton from page load until the first API response.
  // This ensures skeleton is visible continuously — never clears before data arrives.
  const listLoading = (!initialLoadComplete && conversations.length === 0) || loading;

  // ── CRM placeholder conversations ──
  // When CRM phone search returns no results, show placeholder conversations
  // for each sender so the user can pick one and start messaging immediately.
  const placeholderConversations = useMemo((): Conversation[] => {
    if (!phoneSearchActive) return [];
    if (conversations.length > 0 || loading) return [];
    if (senders.length === 0) return [];

    // Get phone from CRM fields or from the search query
    const crmPhone = crmPhoneFields[0]?.value;
    const searchPhone = searchQuery.trim();
    const rawPhone = crmPhone || searchPhone;
    // Apply the default country code before stripping to digits (KAN-46).
    // A CRM-stored local number ("2345644227") must become the full E.164
    // ("+12345644227") here — this value becomes both the placeholder's
    // `contactPhone` and the `to` half of its conversation id, so without
    // this the chat targets "+2345644227" and the first reply is sent to a
    // number that is missing its country code.
    // KAN-50: a number that already carries its own country code is kept as
    // typed. Prepending the default on top of it corrupts the number — a US
    // number pasted without '+' would become +91 1 415…, which matches no
    // conversation and sends nowhere.
    const rawDigits = (rawPhone || '').replace(/\D/g, '');
    const cleanPhone = isFullInternationalNumber(rawDigits)
      ? rawDigits
      : applyCountryCode((rawPhone || '').trim(), preferences.defaultCountryCode).replace(/\D/g, '');
    if (!cleanPhone) return [];

    // Put the active/default sender first so it gets auto-selected
    const defaultId = activeSender?.id || preferences.chatDefaultSender?.id || preferences.inboxDefaultSender?.id;
    const sorted = defaultId
      ? [...senders].sort((a, b) => (a.id === defaultId ? -1 : b.id === defaultId ? 1 : 0))
      : senders;

    return sorted.map((s) => {
      const sNum = s.phoneNumber || '';
      const channel: Channel = s.channel || 'SMS';
      const fromNum = channel === 'WHATSAPP'
        ? (sNum.startsWith('whatsapp:') ? sNum : `whatsapp:${sNum}`)
        : (sNum.startsWith('+') ? sNum : `+${sNum}`);
      const toNum = channel === 'WHATSAPP' ? `whatsapp:+${cleanPhone}` : `+${cleanPhone}`;
      const channelLabel = channel === 'WHATSAPP' ? 'WhatsApp' : 'SMS';
      return {
        id: `${fromNum}::${toNum}`,
        contactName: `Start ${channelLabel} conversation`,
        contactPhone: cleanPhone,
        lastMessage: `via ${cleanSenderLabel(s.label)}`,
        lastMessageTime: new Date(),
        unreadCount: 0,
        channel,
      };
    });
  }, [phoneSearchActive, conversations.length, loading, crmPhoneFields, senders, activeSender, preferences.chatDefaultSender, preferences.inboxDefaultSender, preferences.defaultCountryCode, searchQuery]);

  const displayConversations = placeholderConversations.length > 0 ? placeholderConversations : conversations;

  // ── Auto-select first conversation when phone search completes ──
  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (autoSelectedRef.current) return;
    if (!phoneSearchActive) return;
    if (loading) return;
    if (displayConversations.length === 0) return;
    if (activeConversation) { autoSelectedRef.current = true; return; }

    const firstConv = displayConversations[0];
    if (firstConv) {
      autoSelectedRef.current = true;
      // Placeholder conversations have no messages — skip fetchMessages
      const isPlaceholder = firstConv.contactName.startsWith('Start ') && firstConv.contactName.endsWith(' conversation');
      if (isPlaceholder) {
        useConversationStore.setState({
          activeConversation: firstConv,
          messages: [],
          messagesHasMore: false,
          loadingOlderMessages: false,
          realtimeMessageIds: new Set<string>(),
        });
      } else {
        setActiveConversation(firstConv);
      }
      // Inbox-side rule: only fire when the auto-selected conv has an
      // unread badge. Bonus: avoids a CF roundtrip on every page-load
      // when landing on an already-read conv.
      if (firstConv.unreadCount > 0) {
        markAsRead(firstConv.id);
      }
    }
  }, [phoneSearchActive, loading, initialLoadComplete, activeConversation, displayConversations, setActiveConversation, markAsRead]);

  // No TPA module loaded
  if (!tpaModule) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <EmptyState
          icon={<MessageCircle className="h-8 w-8 text-muted-foreground" />}
          title="No messaging provider"
          description="A messaging provider needs to be configured to view conversations."
          action={
            <a
              href="/inbox"
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <AppWindow className="h-4 w-4" />
              Show Installed Apps
            </a>
          }
        />
      </div>
    );
  }

  if (allSendersHidden) {
    return (
      <div className="flex h-full items-center justify-center">
        <EmptyState
          icon={<MessageCircle className="h-10 w-10 text-muted-foreground" />}
          title="No access"
          description="You don't have permission to view any senders. Contact your admin."
        />
      </div>
    );
  }

  return (
    <div className={cn(
      'flex flex-col overflow-hidden',
      'h-full'
    )}>
      {/* Main content */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Conversation List - sidebar */}
        <div className={cn(
          'flex flex-col overflow-hidden',
          crmRecordContext ? 'w-[380px]' : 'w-[450px]',
          'border-r border-border shrink-0',
          activeConversation ? 'hidden md:flex' : crmRecordContext ? 'flex w-full md:w-[380px]' : 'flex w-full md:w-[450px]'
        )}>
          <ConversationList
            conversations={displayConversations}
            activeConversation={activeConversation}
            loading={listLoading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            searchQuery={searchQuery}
            filterLabel={filterLabel}
            filterAgent={filterAgent}
            chatLabels={chatLabels}
            senderOptions={senderOptions}
            activeSenderId={activeSender?.id || ALL_SENDERS_VALUE}
            onSenderChange={(value) => {
              if (value === ALL_SENDERS_VALUE) {
                setActiveSender(null);
              } else {
                const selected = senders.find((s) => s.id === value);
                if (selected) setActiveSender(selected);
              }
            }}
            onSearchChange={setSearchQuery}
            onSearchByPhone={searchByPhone}
            phoneSearchActive={phoneSearchActive}
            onFilterLabelChange={setFilterLabel}
            onFilterAgentChange={setFilterAgent}
            jumpBefore={jumpBefore}
            onJumpToDate={jumpToConversationsAtDate}
            onOpenKanban={!crmRecordContext && useAppStore.getState().appCodeTPA === 'whatsapp' ? () => {
              const appCode = useAppStore.getState().extensionName;
              if (appCode) {
                window.history.pushState({}, '', `/${appCode}/inbox/kanban`);
              }
              setView('KANBAN');
            } : undefined}
            onSelectConversation={handleSelectConversation}
            onRefresh={refresh}
            onLoadMore={loadMore}
            onNewChat={phoneSearchActive ? undefined : () => setShowNewChat(!showNewChat)}
            mentionCount={mentions.filter((m) => !m.read).length}
            onSelectConversationFromMention={handleSelectConversation}
            activeSender={activeSender}
            isWhatsApp={isWhatsApp}
            tpaName={tpaModule?.name}
            hideFilters={!!crmRecordContext || phoneSearchActive}
            hideSenderPicker={phoneSearchActive}
            visibleSenders={senders}
            className="flex-1"
          />
        </div>

        {/* Chat Thread + Lookup Panel */}
        <div className={cn(
          'flex-1 flex min-h-0 min-w-0 overflow-hidden',
          !activeConversation ? 'hidden md:flex' : 'flex'
        )}>
          {/* Chat Thread - main content */}
          <div className="flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden">
            {/* Wrapper needed because AnimatePresence doesn't participate in flex layout */}
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
              <AnimatePresence mode="wait">
                {activeConversation ? (
                  <motion.div
                    key={activeConversation.id}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.2 }}
                    className="h-full flex flex-col min-h-0"
                  >
                    <ChatThread
                      conversation={activeConversation}
                      messages={messages}
                      loading={messagesLoading}
                      onBack={handleBack}
                      onRefresh={() => {
                        refreshMessages();
                      }}
                      onMessageSent={handleMessageSent}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key={`empty-${emptyStateView}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="h-full flex flex-col min-h-0"
                  >
                    {emptyStateView === 'none' ? (
                      crmRecordContext ? (
                        /* CRM context: show chat placeholder since a chatbox will be auto-selected */
                        <div className="flex-1 flex items-center justify-center">
                          <div className="text-center">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted mx-auto mb-3">
                              <Loader2 className="h-5 w-5 text-muted-foreground/60 animate-spin" />
                            </div>
                            <p className="text-sm text-muted-foreground">Loading conversations...</p>
                          </div>
                        </div>
                      ) : (
                      <div className="flex-1 flex items-center justify-center">
                        <div className="text-center">
                          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-muted mx-auto mb-4">
                            <MessageCircle className="h-10 w-10 text-muted-foreground/40" />
                          </div>
                          <h3 data-testid="empty-state-heading" className="text-lg font-medium text-foreground mb-1">
                            Select a conversation
                          </h3>
                          <p data-testid="empty-state-subtitle" className="text-sm text-muted-foreground max-w-xs mx-auto">
                            Choose a conversation from the sidebar to view messages and reply.
                          </p>
                          <div className="flex items-center justify-center gap-3 mt-6 flex-wrap">
                            {!crmRecordContext && useAppStore.getState().appCodeTPA === 'whatsapp' && (
                              <button
                                onClick={() => {
                                  const appCode = useAppStore.getState().extensionName;
                                  if (appCode) {
                                    window.history.pushState({}, '', `/${appCode}/inbox/kanban`);
                                  }
                                  setView('KANBAN');
                                }}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-background hover:bg-accent text-sm text-foreground transition-colors"
                              >
                                <Columns3 className="h-4 w-4 text-primary/70" />
                                Open Kanban View
                              </button>
                            )}
                            {useAppStore.getState().appCodeTPA === 'twilio' && (
                              <button
                                data-testid="scheduled-messages-btn"
                                onClick={() => setEmptyStateView('scheduled')}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-background hover:bg-accent text-sm text-foreground transition-colors"
                              >
                                <CalendarClock className="h-4 w-4 text-primary/60" />
                                Scheduled Messages
                              </button>
                            )}
                            {useAppStore.getState().appCodeTPA === 'twilio' && (
                              <button
                                data-testid="messages-history-btn"
                                onClick={() => setEmptyStateView('history')}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-background hover:bg-accent text-sm text-foreground transition-colors"
                              >
                                <History className="h-4 w-4 text-muted-foreground" />
                                Messages History
                              </button>
                            )}
                            <button
                              data-testid="reminders-btn"
                              onClick={() => setShowReminders(true)}
                              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-background hover:bg-accent text-sm text-foreground transition-colors"
                            >
                              <Bell className="h-4 w-4 text-amber-500" />
                              Reminders
                            </button>
                          </div>

                          {/* CRM enrichment module trigger. Click → dialog
                              with full chip picker. Drives the same
                              setting as Inbox View Settings. */}
                          <div className="mt-6 flex justify-center">
                            <EnrichmentModulesPicker />
                          </div>
                        </div>
                      </div>
                      )
                    ) : (
                      <InboxPanel
                        view={emptyStateView}
                        onBack={() => setEmptyStateView('none')}
                        onOpenConversation={(conv) => {
                          setActiveConversation(conv);
                          setEmptyStateView('none');
                          setView('CHAT');
                        }}
                      />
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* CRM Lookup Panel — shown when open + conversation active + SaaS has Lookup app */}
          {lookupPanelOpen && lookupSupported && activeConversation && saasModule && (
            lookupUsesIframe
              ? <LookupPanelIframe conversation={activeConversation} />
              : <LookupPanel conversation={activeConversation} />
          )}
        </div>
      </div>

      {/* Error toast */}
      {error && (
        <div className="fixed bottom-4 right-4 z-50 rounded-lg bg-destructive p-3 text-sm text-destructive-foreground shadow-lg max-w-sm">
          {error}
        </div>
      )}

      {/* New Chat Dialog */}
      {showNewChat && (
        <NewChatDialog
          senders={senders}
          defaultSenderId={activeSender?.id}
          onStart={handleNewChat}
          onClose={() => setShowNewChat(false)}
        />
      )}

      {/* Reminders Dialog */}
      <RemindersDialog
        open={showReminders}
        onOpenChange={setShowReminders}
        onOpenConversation={(conv) => {
          setShowReminders(false);
          handleSelectConversation(conv);
        }}
      />

    </div>
  );
}
